import type { DividendChartPoint } from '@app-types/performanceDividend';

/**
 * SP/mobile chart page size for the `配当推移` combo chart.
 * Below the widget mobile breakpoint the chart shows at most five annual
 * points per page (work_items_96). Desktop is unaffected and shows all points.
 */
export const DIVIDEND_MOBILE_CHART_PAGE_SIZE = 5;

const NO_DATA_LABEL = '--';

export interface DividendChartPageResult {
  /** Visible chart points for the requested page (never mutates input). */
  points: DividendChartPoint[];
  /** Clamped current page in the `1..pageCount` range. */
  page: number;
  /** Total pages, minimum stable value of `1`. */
  pageCount: number;
  /** Range label from first/last visible labels, e.g. `21/03 – 25/03`. */
  rangeLabel: string;
  /** Page label derived from state, e.g. `2/2`. */
  pageLabel: string;
  /** Previous page arrow enabled (disabled on the first page). */
  hasPrevious: boolean;
  /** Next page arrow enabled (disabled on the final page). */
  hasNext: boolean;
}

/**
 * Pure chart-pagination helper for the Dividend combo chart.
 *
 * It slices already-mapped chronological points by page. It does not touch the
 * API mapper, React Query keys, or axis bounds; the caller keeps axis scale
 * from the full selected dataset so pages stay visually comparable.
 */
export const getDividendChartPage = (
  points: DividendChartPoint[] | undefined | null,
  requestedPage: number | null,
  pageSize: number = DIVIDEND_MOBILE_CHART_PAGE_SIZE,
): DividendChartPageResult => {
  const allPoints = points ?? [];
  const size =
    Number.isFinite(pageSize) && pageSize > 0
      ? Math.floor(pageSize)
      : DIVIDEND_MOBILE_CHART_PAGE_SIZE;

  const pageCount = Math.max(1, Math.ceil(allPoints.length / size));

  // null preserves latest-page intent through the initial empty/loading state.
  const safeRequested = requestedPage === null
    ? pageCount
    : Number.isFinite(requestedPage) ? Math.floor(requestedPage) : 1;
  const page = Math.min(Math.max(safeRequested, 1), pageCount);

  const start = (page - 1) * size;
  const visible = allPoints.slice(start, start + size);

  const firstLabel = visible[0]?.label ?? NO_DATA_LABEL;
  const lastLabel = visible[visible.length - 1]?.label ?? NO_DATA_LABEL;
  const rangeLabel = `${firstLabel} – ${lastLabel}`;

  return {
    points: visible,
    page,
    pageCount,
    rangeLabel,
    pageLabel: `${page}/${pageCount}`,
    hasPrevious: page > 1,
    hasNext: page < pageCount,
  };
};

export default getDividendChartPage;
